/**
 * LeagueSection — league header with left accent bar + paginated inline game cards.
 */

import React, { useMemo, useCallback, useState } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import type { RefObject } from 'react'
import { Star, StarOff, ChevronDown, ChevronRight } from 'lucide-react-native'
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag'
import { GameInlineCard } from './GameInlineCard'
import { usePagination } from '../../../src/shared/hooks/usePagination'
import { getCreateGamesStepStyles } from './CreateGamesStep.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { MAX_GAMES_PER_CHALLENGE } from '../../../src/create/constants/create.constants'
import type { PickSlip, Game } from '../../../src/create/types/create.types'

const STAR_HIT_SLOP = { top: 10, bottom: 10, left: 10, right: 10 }

export interface LeagueSectionProps {
  league: string
  leagueId?: string
  leagueGames: Game[]
  isFavorite?: boolean
  onToggleFavorite?: (leagueId: string) => void
  getPickSlipForGame: (gameId: string) => PickSlip | undefined
  handlePickSelect: (gameId: string, pick: 'home' | 'draw' | 'away' | null) => void
  selectedGameIds: Set<string>
  selectedCount: number
  picksCountByGameId?: Record<string, number>
  walkthroughFirstRowRef?: RefObject<View | null>
  isWalkthroughGamesStep?: boolean
  /** True when this league holds the spotlighted walkthrough card (drives reactive expand on the first game). */
  isWalkthroughCard?: boolean
  onWalkthroughGameTap?: () => void
}

const LeagueSectionInner: React.FC<LeagueSectionProps> = ({
  league,
  leagueId,
  leagueGames,
  isFavorite = false,
  onToggleFavorite,
  getPickSlipForGame,
  handlePickSelect,
  selectedGameIds,
  selectedCount,
  picksCountByGameId = {},
  walkthroughFirstRowRef,
  isWalkthroughGamesStep = false,
  isWalkthroughCard = false,
  onWalkthroughGameTap,
}) => {
  const theme = useTheme()
  const styles = useMemo(() => getCreateGamesStepStyles(theme), [theme])
  const [isExpanded, setIsExpanded] = useState(true)

  const { displayedItems, hasMore, showMore } = usePagination({
    items: leagueGames,
    initialLimit: 10,
    increment: 10,
  })

  const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), [])

  const handleStarPress = useCallback(() => {
    if (leagueId != null && onToggleFavorite) onToggleFavorite(leagueId)
  }, [leagueId, onToggleFavorite])

  const showStar = leagueId != null && onToggleFavorite != null
  const leagueCountryCode = leagueGames[0]?.leagueCountryCode

  return (
    <View style={styles.leagueSection}>
      {/* League header */}
      <View style={styles.leagueHeaderContainer}>
        <View style={styles.leagueHeader}>
          {/* Left purple accent bar */}
          <View style={styles.leagueHeaderAccent} />

          {showStar && (
            <Pressable
              onPress={handleStarPress}
              hitSlop={STAR_HIT_SLOP}
              style={({ pressed }) => [
                styles.leagueStarButton,
                Platform.select({ web: { transition: 'opacity 0.15s' } as unknown as object }),
                pressed && styles.leagueStarButtonPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel={isFavorite ? `Unfavorite ${league}` : `Favorite ${league}`}
              accessibilityState={{ checked: isFavorite }}
            >
              {isFavorite
                ? <Star size={18} color="#FCD34D" />
                : <StarOff size={18} color="#6B7280" />
              }
            </Pressable>
          )}

          {leagueCountryCode != null && leagueCountryCode.length > 0 && (
            <View style={styles.leagueHeaderFlagWrap}>
              <CountryFlag
                countryCode={leagueCountryCode}
                size="compact"
                accessibilityLabel={`${league} country flag`}
              />
            </View>
          )}

          <Text style={styles.leagueTitle} numberOfLines={1}>{league}</Text>

          <Text style={styles.leagueMatchCount}>{leagueGames.length} matches</Text>

          <SoundPressable
            tapSoundId={isExpanded ? SOUND_IDS.UI_SECTION_COLLAPSE : SOUND_IDS.UI_SECTION_EXPAND}
            onPress={toggleExpanded}
            accessibilityRole="button"
            accessibilityLabel={isExpanded ? `Collapse ${league}` : `Expand ${league}`}
            accessibilityState={{ expanded: isExpanded }}
            testID={`league-section-${league.replace(/\s+/g, '-').toLowerCase()}-header`}
          >
            {isExpanded
              ? <ChevronDown size={18} color={theme.primary} />
              : <ChevronRight size={18} color={theme.primary} />
            }
          </SoundPressable>
        </View>
      </View>

      {isExpanded && (
        <View style={styles.gamesContainer}>
          {displayedItems.map((game, gameIndex) => {
            const isAlreadySelected = selectedGameIds.has(game.id)
            const isDisabled = !isAlreadySelected && selectedCount >= MAX_GAMES_PER_CHALLENGE

            return (
              <GameInlineCard
                key={game.id}
                game={game}
                getPickSlipForGame={getPickSlipForGame}
                handlePickSelect={handlePickSelect}
                isDisabled={isDisabled}
                picksCount={picksCountByGameId[game.id] ?? 0}
                cardRef={gameIndex === 0 ? walkthroughFirstRowRef : undefined}
                isWalkthroughGamesStep={isWalkthroughGamesStep}
                isWalkthroughCard={gameIndex === 0 ? isWalkthroughCard : undefined}
                onWalkthroughGameTap={onWalkthroughGameTap}
              />
            )
          })}
          {hasMore && (
            <Pressable
              style={({ pressed }) => [
                styles.showMoreButton,
                Platform.select({ web: { transition: 'opacity 0.15s' } as unknown as object }),
                pressed && styles.showMoreButtonPressed,
              ]}
              onPress={showMore}
            >
              <Text style={styles.showMoreText}>
                Show More ({leagueGames.length - displayedItems.length} remaining)
              </Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  )
}

export const LeagueSection = React.memo(LeagueSectionInner)
